import React from 'react';
import {
  Card,
  CardContent,
  Divider,
  Grid,
  Step,
  StepButton,
  StepLabel,
  Stepper,
} from '@material-ui/core';
import clsx from 'clsx';
import { useGlobal } from '@rcg/ui-react';
import { useReactiveVar } from '@apollo/client';
import InfoTooltip from '../InfoTooltip';
import useStyles from './useStyles';
import PassengerDetailForm from './PassengerDetailForm';
import passengerValidationSchema from '../../utils/passengerInfo';
import Dictionary from '../../utils/dictionary';
import {
  bookingProfileVar,
  heldReservationVar,
  searchParamsVar,
} from '../../operations/localState/reactiveVariables';
import { setTravelerInformation } from '../../operations/localState/setSearchParams';
import { setBookingEmail } from '../../operations/localState/setBookingProfile';

function PassengerIcon() {
  return <div> </div>;
}

const PassengersDetailsWizard = ({ setShouldDisableCta = () => {}, passengerDetailsFormRef }) => {
  const classes = useStyles();
  const { renderContent } = useGlobal();
  const bookingProfile = useReactiveVar(bookingProfileVar);
  const heldReservation = useReactiveVar(heldReservationVar);
  const lastSearchParams = useReactiveVar(searchParamsVar);
  const [activeStep, setActiveStep] = React.useState(0);
  const steps = [
    renderContent(
      Dictionary.airPrimaryPassengerLabel.key,
      Dictionary.airPrimaryPassengerLabel.defaultValue,
    ),
  ];
  const initialPassengerInfo = [];

  const marketingAirlinesForCurrentReservation = [];
  const getFlightsSegments = () => {
    const flightSegments = [];
    if (heldReservation.holdFlight.inboundFlight) {
      flightSegments.push(...heldReservation.holdFlight.inboundFlight.flightSegments);
    }
    if (heldReservation.holdFlight.outboundFlight) {
      flightSegments.push(...heldReservation.holdFlight.outboundFlight.flightSegments);
    }
    return flightSegments;
  };
  getFlightsSegments().forEach(({ marketingAirline }) => {
    if (
      marketingAirline &&
      marketingAirline.code &&
      !marketingAirlinesForCurrentReservation.some(
        (savedMarketingAirline) => savedMarketingAirline.code === marketingAirline.code,
      )
    ) {
      marketingAirlinesForCurrentReservation.push(marketingAirline);
    }
  });

  lastSearchParams.guests.forEach((guest, index) => {
    if (lastSearchParams.guests.length > 1 && index !== 0) {
      steps.push(
        `${renderContent(
          Dictionary.airPassengerLabel.key,
          Dictionary.airPassengerLabel.defaultValue,
        )} ${index + 1}`,
      );
    }
    initialPassengerInfo[index] = {
      guestID: guest.id,
      firstName: guest.flightGuest.firstName,
      middleName: '',
      lastName: guest.flightGuest.lastName,
      knownTravelerNumber: '',
      redressNumber: '',
      loyaltyPrograms: marketingAirlinesForCurrentReservation.map((marketingAirline) => {
        return {
          frequentFlyerProgramCode: marketingAirline.code,
          frequentFlyerProgramName: marketingAirline.name,
          frequentFlyerNumber: '',
        };
      }),
    };
    if (index === 0) {
      initialPassengerInfo[index].email = bookingProfile.bookingEmail || '';
    }
  });
  const [passengerInfo, setPassengerInfo] = React.useState(initialPassengerInfo);

  const handleStep = (step) => () => {
    if (step === 0) {
      setTimeout(() => {
        passengerDetailsFormRef.current.handleSubmit();
      }, 100);
    }
    setActiveStep(step);
  };

  const handleSubmit = (values) => {
    const passengers = { ...passengerInfo };
    passengers[activeStep] = values;
    setPassengerInfo(passengers);
    setBookingEmail(passengers[0].email);
    setTravelerInformation(passengers);
  };

  function getStepContent(step) {
    return (
      <PassengerDetailForm
        index={step}
        passengerInfo={passengerInfo[step]}
        currentValidationSchema={
          passengerValidationSchema({
            airPassengerInfoFirstNameErrorRequired: renderContent(
              Dictionary.airPassengerInfoFirstNameErrorLabel.key,
              Dictionary.airPassengerInfoFirstNameErrorLabel.defaultValue,
            ),
            airPassengerInfoLastNameErrorRequired: renderContent(
              Dictionary.airPassengerInfoLastNameErrorLabel.key,
              Dictionary.airPassengerInfoLastNameErrorLabel.defaultValue,
            ),
            airPassengerInfoEmailErrorRequired: renderContent(
              Dictionary.airPassengerInfoEmailErrorLabel.key,
              Dictionary.airPassengerInfoEmailErrorLabel.defaultValue,
            ),
          })[step]
        }
        onSubmit={(values) => handleSubmit(values)}
        handleCtaDisable={(isValid) => {
          if (step === 0) {
            setShouldDisableCta(isValid);
          }
        }}
        passengerDetailsFormRef={passengerDetailsFormRef}
      />
    );
  }

  return (
    <div className={classes.passengerDetailsWizard}>
      <Grid container>
        <Grid item xl={12} lg={12} md={12} sm={12} className={classes.fullWidth}>
          <Card className={classes.wizardWrapper} id="wizard-wrapper">
            <div className={`${classes.wizardTitle} ${classes.titleWithTooltip}`}>
              {renderContent(
                Dictionary.airPassengerDetailsTitle.key,
                Dictionary.airPassengerDetailsTitle.defaultValue,
              )}
              <InfoTooltip
                dataTestId="info-tooltip-component"
                description={renderContent(
                  Dictionary.airPassengerDetailsSubtitle.key,
                  Dictionary.airPassengerDetailsSubtitle.defaultValue,
                )}
              />
            </div>
            <CardContent className={classes.wizardStepper}>
              <Divider />
              <Stepper
                activeStep={activeStep}
                nonLinear
                className={classes.wizardInline}
                connector={<></>}>
                {steps.map((label, index) => (
                  <Step key={label} className={classes.wizardStepWrapper}>
                    <StepButton
                      onClick={handleStep(index)}
                      className={classes.wizardStepButton}
                      id={`step-button-${index}`}>
                      <StepLabel StepIconComponent={PassengerIcon}>
                        <span
                          className={clsx(classes.stepRoot, {
                            [classes.activeStepIcon]: activeStep === index,
                          })}>
                          {label}
                        </span>
                      </StepLabel>
                    </StepButton>
                  </Step>
                ))}
              </Stepper>
              {getStepContent(activeStep)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default PassengersDetailsWizard;
